// Agent tools that let the chat drive page navigation: open a data-graph
// scope in the main Explore column, or open a saved Studio item
// (kernel / canvas / workflow). See navigationStore.js for the bridge
// and navIntent.js for the validation.
//
// Hybrid autonomy (per O-2084 product decision):
//   • docked AND the user explicitly asked (`requested: true`)
//       → apply the navigation directly (a new tab appears beside the
//         docked chat — additive, the user's current tab survives).
//   • otherwise (volunteered, or the chat is a floating overlay)
//       → return an "offer": a navSuggestion the transcript renders as a
//         one-click "Open … →" chip. Overlay navigations always offer so
//         we never yank the page out from behind the overlay.

import { Type } from "@earendil-works/pi-ai";

import { getDockBodyHost } from "./dockStore.js";
import { requestNavigation } from "./navigationStore.js";
import { coerceJsonArg } from "./coerceJsonArg.js";
import { navigableScopeDims, scopeActivityStatus, rangeToTimeRange } from "./analyticsClient.js";
import { toApiRange } from "./shell.js";
import { readUrlParam } from "./urlParams.js";
import { findKernelInCatalogByCommand } from "./kernelStore.js";
import { findCanvasTemplateBySlug } from "./canvasTemplateStore.js";
import { loadWorkflows } from "./workflowStore.js";
import {
  validateExploreScope,
  validateStudioTarget,
  describeScope,
  shouldNavigateDirectly,
  navStatusBlocks,
  STUDIO_VIEWS,
} from "./navIntent.js";

function isDocked() {
  return Boolean(getDockBodyHost());
}

function toolError(text) {
  return { isError: true, content: [{ type: "text", text }] };
}

// The existence checks fail closed on "unknown" (a probe error). Since
// that refuses a navigation the user asked for, absorb a single transient
// backend/IDB blip first: one retry after a short pause before giving up.
// "active"/"empty"/"exists"/"missing" are decisive — only "unknown" retries.
const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
async function checkWithRetry(probe) {
  const first = await probe();
  if (first !== "unknown") return first;
  await sleep(300);
  return probe();
}

// Resolve the time range the landed Explore tab will inherit (?range=,
// default 1M) into a query timeRange — so the existence probe checks the
// SAME window the user will be looking at.
function currentTimeRange() {
  return rangeToTimeRange(toApiRange(readUrlParam("range") || "1M"));
}

// Does a saved Studio item exist? Passing tenantId=null lets each store
// fall back to the current tenant (getCurrentTenantIdSync), matching how
// the studio sidebar lists them. Returns "exists" | "missing" |
// "unknown" (lookup threw) — caller fails closed on the latter two.
async function studioItemStatus(view, itemId) {
  try {
    if (view === "kernels") return findKernelInCatalogByCommand(null, itemId) ? "exists" : "missing";
    if (view === "canvases") return findCanvasTemplateBySlug(null, itemId) ? "exists" : "missing";
    if (view === "workflows") {
      // Match on `command` only — navigation opens a workflow by its
      // command (makeWorkflowTab(command)), so accepting an internal id
      // here would pass the gate then open a tab that can't resolve.
      const all = await loadWorkflows(null);
      return all.some((w) => w.command === itemId) ? "exists" : "missing";
    }
    return "missing";
  } catch {
    return "unknown";
  }
}

// Apply directly (docked + requested) or return an offer. `intent` is a
// validated navigation intent; `label` is the human description.
function dispatch(intent, label, requested) {
  if (shouldNavigateDirectly(requested, isDocked())) {
    requestNavigation(intent);
    return {
      content: [{ type: "text", text: `Opened ${label} in the main column.` }],
      details: { kind: "navApplied", intent, label },
    };
  }
  return {
    content: [{
      type: "text",
      text: `Offered to open ${label}. The user has a one-click button to open it.`,
    }],
    details: { kind: "navSuggestion", intent, label },
  };
}

export function createNavigationTools() {
  const openInExplore = {
    name: "open_in_explore",
    description: [
      "Open a data-graph view in the MAIN Explore column as a tab — so the user sees the live, drillable data beside the chat (especially when the chat is docked). Use this when the user asks to see / go to / pull up part of the graph, or when a live view clearly beats an inline answer.",
      "",
      "scopePath is an array of steps, each { dim, ids }. `ids` MUST be real ids you got from an analytics_query grouped by that dim — never invent them. Steps nest (the 2nd step filters within the 1st).",
      `Navigable dims: ${navigableScopeDims().join(", ")}.`,
      "",
      "Set `requested: true` ONLY when the user explicitly asked to be taken there — then, if the chat is docked, the tab opens immediately. Otherwise (you're volunteering, or the chat is a floating overlay) the user gets a one-click 'Open in Explore' chip instead. Opening a scope that's already a tab just activates it.",
    ].join("\n"),
    parameters: Type.Object({
      scopePath: Type.Array(
        Type.Object({
          dim: Type.String({ description: "dashboard dim key, e.g. endpoints / agents / sessions / clusters / models / tools / skills / mcp_servers" }),
          ids: Type.Array(Type.String(), { description: "value ids for this dim, taken from analytics_query results" }),
        }),
        { description: "ordered scope steps; later steps nest within earlier ones" },
      ),
      requested: Type.Optional(Type.Boolean({ description: "true only if the user explicitly asked to navigate" })),
    }),
    async execute(_id, args) {
      const scopePath = coerceJsonArg(args.scopePath);
      const v = validateExploreScope(scopePath);
      if (!v.ok) return toolError(v.error);
      const label = describeScope(v.scopePath);
      // Existence check (fails CLOSED — the gate exists to prevent dead
      // navigations). Probe the SAME range the tab will land in, so we
      // catch both invented ids and scopes that are simply empty in the
      // user's current window.
      const timeRange = currentTimeRange();
      const status = await checkWithRetry(() => scopeActivityStatus(v.scopePath, timeRange));
      if (navStatusBlocks(status)) {
        return toolError(status === "empty"
          ? `No data found at scope "${label}" in the selected time range. Use ids from an analytics_query result or widen the date range.`
          : `Couldn't verify data at scope "${label}". Try again shortly.`);
      }
      const intent = { kind: "explore", scopePath: v.scopePath, label };
      return dispatch(intent, label, Boolean(args.requested));
    },
  };

  const openStudioItem = {
    name: "open_studio_item",
    description: [
      "Open a saved Studio item — a kernel, canvas, or workflow — in its own Studio tab. Use when the user asks to open / view / go to a saved item by name.",
      `view: ${STUDIO_VIEWS.join(" | ")}. itemId: the kernel command, canvas slug, or workflow command.`,
      "Set `requested: true` only when the user explicitly asked; same docked-vs-offer behavior as open_in_explore. Opening an item that's already a tab just activates it.",
    ].join("\n"),
    parameters: Type.Object({
      view: Type.String({ description: `one of: ${STUDIO_VIEWS.join(", ")}` }),
      itemId: Type.String({ description: "kernel command / canvas slug / workflow command" }),
      requested: Type.Optional(Type.Boolean({ description: "true only if the user explicitly asked to navigate" })),
    }),
    async execute(_id, args) {
      const v = validateStudioTarget(args.view, args.itemId);
      if (!v.ok) return toolError(v.error);
      const kind = v.view === "kernels" ? "kernel" : v.view === "canvases" ? "canvas" : "workflow";
      const label = `${kind} ${v.itemId}`;
      // Existence check (fails CLOSED) — don't navigate to a Studio item
      // that isn't saved.
      const status = await checkWithRetry(() => studioItemStatus(v.view, v.itemId));
      if (navStatusBlocks(status)) {
        return toolError(status === "missing"
          ? `No saved ${kind} named "${v.itemId}". Check the name, or list what exists first.`
          : `Couldn't verify ${kind} "${v.itemId}". Try again shortly.`);
      }
      const intent = { kind: "studio", view: v.view, itemId: v.itemId, label };
      return dispatch(intent, label, Boolean(args.requested));
    },
  };

  return [openInExplore, openStudioItem];
}
